import { css, html, nothing } from "lit";
import { ifDefined } from "lit-html/directives/if-defined.js";
import { query, state } from "lit/decorators.js";
import IMask from "imask/esm/imask.js";
import "imask/esm/masked/pattern.js";
import "imask/esm/masked/function.js";
import "imask/esm/masked/enum.js";
import "../Input/index.js";
import "../Badge/index.js";
import "../Tooltip/index.js";
import "../../atom/Icon/index.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { rif } from "../../../helpers/renderIf.js";
import { BaseComponent } from "../../BaseComponent.js";
import { parseTime, getMaskOptions, timeToString, getInputClasses } from "./utils.js";
import { preventDefault } from "../../../decorators/preventDefault.js";
import { consume, provide } from "@lit/context";
import { groupContext } from "../FormGroup/index.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
let DsfTimepicker = class extends BaseComponent {
  constructor() {
    var _a;
    super(...arguments);
    this.value = "";
    this.dsf_error = false;
    this.dsf_success = false;
    this.dsf_size = "medium";
    this.disabled = false;
    this.readonly = false;
    this.dsf_time_format = "24";
    this.nestedGroupCtx = {
      getLabel: ((_a = this == null ? void 0 : this.groupCtx) == null ? void 0 : _a.getLabel) || (() => ""),
      getPosition: () => {
        var _a2;
        return ((_a2 = this == null ? void 0 : this.groupCtx) == null ? void 0 : _a2.getPosition(this)) || "ungrouped";
      }
    };
    this._displayValue = "";
  }
  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;
    await this.dsfInputElement.updateComplete;
    this.initMask();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.destroyMask();
  }
  update(changedProps) {
    if (changedProps.has("value")) {
      if (!this.value || !parseTime(this.value, "24")) {
        this.value = "";
      }
      this.resetDisplayValue();
    }
    if (changedProps.has("dsf_time_format")) {
      if (this._mask) {
        this.destroyMask();
        this.initMask();
        this.resetDisplayValue();
      }
    }
    super.update(changedProps);
  }
  initMask() {
    this._mask = IMask(this.dsfInputElement.inputElement, getMaskOptions(this.dsf_time_format));
    this._mask.on("accept", () => {
      this.handleMaskInput(this._mask.value, this._mask.unmaskedValue);
    });
  }
  destroyMask() {
    var _a, _b;
    (_a = this._mask) == null ? void 0 : _a.off("accept");
    (_b = this._mask) == null ? void 0 : _b.destroy();
    this._mask = void 0;
  }
  resetDisplayValue() {
    let formattedValue = "";
    if (this.value) {
      const time = parseTime(this.value, "24");
      formattedValue = time ? timeToString(time, this.dsf_time_format) : "";
    }
    if (this._mask) {
      this._mask.value = formattedValue;
    } else {
      this._displayValue = formattedValue;
    }
  }
  handleInput() {
  }
  handleMaskInput(maskedValue, unmaskedValue) {
    this._displayValue = maskedValue;
    if (unmaskedValue === ":") {
      this.handleUserInput(void 0);
    } else {
      const time = parseTime(unmaskedValue, this.dsf_time_format);
      if (time) {
        this.handleUserInput(time);
      }
    }
  }
  handleUserInput(newTime) {
    const oldValue = this.value;
    this.value = newTime ? timeToString(newTime, "24") : "";
    if (oldValue !== this.value) {
      this.emit({ name: "input", payload: this.value });
    }
  }
  handleInputBlur() {
    this.resetDisplayValue();
  }
  render() {
    const badge = rif(
      this.dsf_badge,
      this.dsf_badge_tooltip ? html`
            <dsf-tooltip dsf_size="small" exportparts="dropdown: badge-tooltip">
              <dsf-badge class="hoverable-badge" slot="anchor" dsf_square dsf_size="small"
                >${this.dsf_badge}</dsf-badge
              >
              <div>${this.dsf_badge_tooltip}</div>
            </dsf-tooltip>
          ` : html`<dsf-badge dsf_square dsf_size="small">${this.dsf_badge}</dsf-badge>`
    );
    let icon = nothing;
    if (this.dsf_icon_right) {
      const iconTemplate = html`<dsf-icon
        slot="anchor"
        part="icon-right"
        class=${`h-16 w-16 ${this.dsf_icon_right_tooltip ? "cursor-pointer" : ""}`}
        dsf_name=${ifDefined(this.dsf_icon_right)}
      ></dsf-icon>`;
      icon = rif(
        this.dsf_icon_right,
        this.dsf_icon_right_tooltip ? html`
              <dsf-tooltip dsf_size="small" exportparts="dropdown: icon-right-tooltip">
                ${iconTemplate}
                <div>${this.dsf_icon_right_tooltip}</div>
              </dsf-tooltip>
            ` : iconTemplate
      );
    }
    const slotContent = rif(
      !!this.dsf_badge || !!this.dsf_icon_right,
      html`<div class="flex items-center gap-4">${badge}${icon}</div>`
    );
    return html`
      <dsf-input
        part="input"
        class=${getInputClasses(this)}
        .value=${this._displayValue}
        .disabled=${this.disabled}
        .readonly=${this.readonly}
        .dsf_error=${this.dsf_error}
        .dsf_success=${this.dsf_success}
        .dsf_size=${this.dsf_size}
        .dsf_placeholder=${this.dsf_placeholder}
        .dsf_message=${this.dsf_message}
        .dsf_label=${this.dsf_label}
        .dsf_name=${this.dsf_name}
        .autocomplete=${this.autocomplete}
        .autofocus=${this.autofocus}
        .dsf_min=${this.dsf_min}
        .dsf_max=${this.dsf_max}
        @input=${this.handleInput}
        @blur=${this.handleInputBlur}
      >
        ${slotContent}
      </dsf-input>
    `;
  }
};
DsfTimepicker.styles = [
  ...BaseComponent.styles,
  css`
      :host {
        display: inline-flex;
      }

      .hoverable-badge::part(badge) {
        cursor: pointer;
      }

      .hoverable-badge::part(badge):hover {
        background: rgb(var(--dsf-color-neutral-200));
      }
    `
];
__decorateClass([
  prop({ type: String })
], DsfTimepicker.prototype, "dsf_badge", 2);
__decorateClass([
  prop({ type: String })
], DsfTimepicker.prototype, "dsf_badge_tooltip", 2);
__decorateClass([
  prop({
    type: String,
    reflect: true,
    converter: {
      fromAttribute: (attr) => parseTime(attr, "24") ? attr : ""
    }
  })
], DsfTimepicker.prototype, "value", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfTimepicker.prototype, "dsf_error", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfTimepicker.prototype, "dsf_success", 2);
__decorateClass([
  prop({
    type: String,
    reflect: true,
    validator: (value) => ["medium", "small"].includes(value)
  })
], DsfTimepicker.prototype, "dsf_size", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfTimepicker.prototype, "disabled", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfTimepicker.prototype, "readonly", 2);
__decorateClass([
  prop({ type: String })
], DsfTimepicker.prototype, "dsf_placeholder", 2);
__decorateClass([
  prop({ type: String })
], DsfTimepicker.prototype, "dsf_message", 2);
__decorateClass([
  prop({ type: String })
], DsfTimepicker.prototype, "dsf_label", 2);
__decorateClass([
  prop({ type: String })
], DsfTimepicker.prototype, "dsf_name", 2);
__decorateClass([
  prop({ type: String })
], DsfTimepicker.prototype, "autocomplete", 2);
__decorateClass([
  prop({ type: String })
], DsfTimepicker.prototype, "dsf_min", 2);
__decorateClass([
  prop({ type: String })
], DsfTimepicker.prototype, "dsf_max", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfTimepicker.prototype, "dsf_time_format", 2);
__decorateClass([
  prop({ type: String })
], DsfTimepicker.prototype, "dsf_icon_right", 2);
__decorateClass([
  prop({ type: String })
], DsfTimepicker.prototype, "dsf_icon_right_tooltip", 2);
__decorateClass([
  query("dsf-input")
], DsfTimepicker.prototype, "dsfInputElement", 2);
__decorateClass([
  consume({ context: groupContext, subscribe: true }),
  prop({ attribute: false })
], DsfTimepicker.prototype, "groupCtx", 2);
__decorateClass([
  provide({ context: groupContext })
], DsfTimepicker.prototype, "nestedGroupCtx", 2);
__decorateClass([
  state()
], DsfTimepicker.prototype, "_displayValue", 2);
__decorateClass([
  preventDefault
], DsfTimepicker.prototype, "handleInput", 1);
DsfTimepicker = __decorateClass([
  defineElement("dsf-timepicker")
], DsfTimepicker);
export {
  DsfTimepicker
};
